import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import toast from "components/ui/toast/Toast";
import {
  useBidOrchestratorStream,
  useBidOrchestratorStreamContext,
} from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { routeSegments } from "lib/routes";
import { BidReportType } from "lib/types/graphQLEnums";
import BidReportLoadingState from "./BidReportLoadingState";

export type BidReportLoadingType =
  | typeof routeSegments.qualificationReport
  | typeof routeSegments.requirementSummary;

export const BID_REPORT_LOADING_COPY: Record<
  BidReportLoadingType,
  { title: string; description: string }
> = {
  [routeSegments.qualificationReport]: {
    title: "Analysing documents",
    description:
      "Stotles AI is analysing your tender documents to answer your qualification questions. Feel free to leave this page and come back later.",
  },
  [routeSegments.requirementSummary]: {
    title: "Analysing documents",
    description:
      "We're just analysing your tender documents to extract the bid requirements. Feel free to leave this page and come back later.",
  },
};

type Props = {
  bidId: string;
  type: BidReportLoadingType;
};

/**
 * Purgatory page mounted between Generate-report submission and the moment
 * the backend emits the report's `*_started` event.
 *
 * The actual dispatch of `startBidOrchestrator` lives in the app-wide orchestrator
 * driver (the `useBidOrchestratorDriver` hook, mounted in AppShell) so it survives
 * the user navigating away from this page mid-upload. All this component does is:
 *  - render the loading copy + Back button,
 *  - hand off to the report page once the `*_started` event publishes the id,
 *  - bail back to the bid page on a terminal failure.
 */
export default function BidReportLoadingPage({ bidId, type }: Props) {
  const navigate = useNavigate();
  const { entry, hasFailed } = useBidOrchestratorStream(bidId);
  const { queuedBidOrchestrators } = useBidOrchestratorStreamContext();
  const { filesProcessing } = useDocumentUploadProgress(bidId);

  const isQualification = type === routeSegments.qualificationReport;
  const expectedReportType = isQualification
    ? BidReportType.Qualification
    : BidReportType.RequirementSummary;
  const reportId = isQualification
    ? entry?.qualificationReportId
    : entry?.requirementSummaryReportId;

  // Mount guard: this purgatory page only makes sense when something for THIS
  // report type is in flight. "In flight" means any of:
  //  - an entry exists whose `generates` includes this type (run dispatched),
  //  - a queued orchestrator for this type exists (driver hasn't dispatched yet),
  //  - an upload is still settling (the driver is waiting on it).
  // None of those → direct visit (bookmark / refresh after the run finished).
  // Bounce back to the bid page so the user sees the refreshed CTAs instead
  // of waiting forever for a `*_started` event that will never come.
  const entryGenerates = isQualification
    ? entry?.generates.qualificationReport
    : entry?.generates.requirementSummary;
  const entryMatches = !!entry && entryGenerates !== false;
  const queuedOrchestrator = queuedBidOrchestrators[bidId];
  const queuedOrchestratorMatches = queuedOrchestrator?.reportType === expectedReportType;
  const shouldBail = !entryMatches && !queuedOrchestratorMatches && !filesProcessing;
  useEffect(() => {
    if (!shouldBail) return;
    void navigate({ to: `/bids/${bidId}`, replace: true });
  }, [shouldBail, bidId, navigate]);

  // The `*_started` event publishes the persisted report id onto the entry.
  // Its presence on the stream entry is our "ready to hand off" signal —
  // once we see it, the report row exists and the questions are seeded, so
  // the destination page can render the streaming content immediately.
  useEffect(() => {
    if (!reportId) return;
    void navigate({
      to: `/bids/${bidId}/${type}/${reportId}`,
      replace: true,
    });
  }, [reportId, bidId, type, navigate]);

  useEffect(() => {
    if (!hasFailed) return;
    toast.error("Failed to create bid report");
    void navigate({ to: `/bids/${bidId}`, replace: true });
  }, [hasFailed, bidId, navigate]);

  const { title, description } = BID_REPORT_LOADING_COPY[type];
  return <BidReportLoadingState title={title} description={description} bidId={bidId} />;
}
